/**
 * Promo Codes Page — Light + Accent Stripe Design
 * Compact 2-col form with live preview, search/filter table, analytics modal
 */
import * as React from 'react'
import axios from 'axios'
import { GluonEnvironment } from '@numeo/types-core'
import { getStoredEnvironment } from 'setup/SetupAxios'
import { CircularProgress } from '@mui/joy'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import ModalClose from '@mui/joy/ModalClose'
import { ContentCopy, LocalOffer, BarChart, Block, CheckCircle, Close, Visibility, ExpandLess, ExpandMore } from '@mui/icons-material'
import { motion } from 'framer-motion'
import { billingApi } from '../billing/api'
import { StripeProductItem } from '../billing/types'

const ease = [0.23, 1, 0.32, 1]
const getSubscribeBase = () => (getStoredEnvironment() === GluonEnvironment.PRODUCTION ? 'https://agent.numeo.ai' : 'https://dev-agent.numeo.ai')

// ─── Types ──────────────────────────────────────────────────────

interface CouponSummary {
    id: string
    name: string
    discountPercent: number
    duration: string
    durationInMonths: number | null
    plans: string[]
    codeCount: number
    totalRedemptions: number
    trialDays: number | null
}
interface PromoCode {
    id: string
    code: string
    couponId: string
    influencerId: string
    influencerName: string
    influencerTier: string
    commissionPercent: number
    discountPercent: number
    duration: string
    durationInMonths: number | null
    plans: string[]
    maxRedemptions: number | null
    timesRedeemed: number
    expiresAt: number | null
    active: boolean
    created: number
    trialDays: number | null
}
interface PromoAnalytics {
    code: string
    influencerName: string
    commissionPercent: number
    totalSignups: number
    byPlan: Record<string, number>
    totalRevenue: number
    totalCommission: number
    dailySignups: Array<{ date: string; count: number; byPlan: Record<string, number> }>
    users: Array<{ email: string; projectName: string; plan: string; price: number; commission: number; joinedAt: string }>
}

function formatDuration(duration: string, months: number | null): string {
    if (duration === 'forever') return 'Forever'
    if (duration === 'once') return 'First payment'
    if (duration === 'repeating' && months) return `${months} month${months > 1 ? 's' : ''}`
    return duration
}
function formatDate(ts: number): string {
    return new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
function formatCurrency(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`
}

// ─── Product scope (plans tokens sent to promo creation endpoints) ──

type PromoProduct = 'extension' | 'tms' | 'mobile'

const PRODUCT_TABS: Array<{ id: PromoProduct; label: string }> = [
    { id: 'extension', label: 'Extension' },
    { id: 'tms', label: 'TMS' },
    { id: 'mobile', label: 'Mobile' },
]

const SCOPE_OPTIONS: Record<PromoProduct, Array<{ label: string; tokens: string[] }>> = {
    extension: [
        { label: 'Pro + Ultra (current)', tokens: ['PRO', 'ULTRA'] },
        { label: 'Pro only', tokens: ['PRO'] },
        { label: 'Ultra only', tokens: ['ULTRA'] },
    ],
    tms: [
        { label: 'TMS Pro only', tokens: ['TMS_PRO'] },
        { label: 'TMS Basic only', tokens: ['TMS_BASIC'] },
        { label: 'Basic + Pro', tokens: ['TMS_BASIC', 'TMS_PRO'] },
    ],
    mobile: [
        { label: 'Max + Platinum', tokens: ['ULTRA_MOBILE', 'PLATINUM_MOBILE'] },
        { label: 'Go only', tokens: ['PRO_MOBILE'] },
        { label: 'Max only', tokens: ['ULTRA_MOBILE'] },
        { label: 'Platinum only', tokens: ['PLATINUM_MOBILE'] },
    ],
}

const TOKEN_LABELS: Record<string, { product: 'Ext' | 'TMS' | 'Mobile'; name: string }> = {
    PRO: { product: 'Ext', name: 'Pro' },
    ULTRA: { product: 'Ext', name: 'Ultra' },
    TMS: { product: 'TMS', name: '' }, // legacy collapsed token (Basic/Pro not distinguishable in list rows)
    TMS_BASIC: { product: 'TMS', name: 'Basic' },
    TMS_PRO: { product: 'TMS', name: 'Pro' },
    PRO_MOBILE: { product: 'Mobile', name: 'Go' },
    ULTRA_MOBILE: { product: 'Mobile', name: 'Max' },
    PLATINUM_MOBILE: { product: 'Mobile', name: 'Platinum' },
}

const SCOPE_BADGE_CLS: Record<string, string> = {
    Ext: 'bg-blue-100 text-blue-700',
    TMS: 'bg-emerald-100 text-emerald-700',
    Mobile: 'bg-violet-100 text-violet-700',
}

/** Group plan tokens into per-product scope badges, e.g. "Ext: Pro+Ultra", "TMS: Basic", "Mobile: Max+Platinum". */
function scopeBadges(plans: string[]): Array<{ label: string; cls: string }> {
    const groups = new Map<string, string[]>()
    const unknown: string[] = []
    for (const token of plans) {
        const info = TOKEN_LABELS[token]
        if (!info) {
            unknown.push(token)
            continue
        }
        const names = groups.get(info.product) || []
        if (info.name) names.push(info.name)
        groups.set(info.product, names)
    }
    const badges: Array<{ label: string; cls: string }> = []
    for (const [product, names] of groups) badges.push({ label: names.length > 0 ? `${product}: ${names.join('+')}` : product, cls: SCOPE_BADGE_CLS[product] })
    for (const token of unknown) badges.push({ label: token, cls: 'bg-gray-100 text-gray-600' })
    return badges
}

function ScopeBadges({ plans }: { plans: string[] }) {
    const badges = scopeBadges(plans)
    if (badges.length === 0) return <span className="text-[10px] text-gray-400">None</span>
    return (
        <div className="flex flex-wrap gap-1">
            {badges.map((b) => (
                <span key={b.label} className={`text-[9px] font-bold px-1.5 py-0 rounded whitespace-nowrap ${b.cls}`}>
                    {b.label}
                </span>
            ))}
        </div>
    )
}

interface ScopeSelectorProps {
    radioName: string
    product: PromoProduct
    scopeIdx: number
    onProduct: (p: PromoProduct) => void
    onScope: (i: number) => void
    showOldPlans: boolean
    onToggleOldPlans: () => void
    oldPlanProducts: StripeProductItem[]
    loadingOldPlans: boolean
    selectedOldPlanIds: string[]
    onToggleOldPlan: (id: string) => void
}

/** Product selector + product-dependent applies-to radios + optional "old plans" (legacy Stripe products → extraProductIds). */
function ScopeSelector(props: ScopeSelectorProps) {
    const { radioName, product, scopeIdx, onProduct, onScope, showOldPlans, onToggleOldPlans, oldPlanProducts, loadingOldPlans } = props
    const { selectedOldPlanIds, onToggleOldPlan } = props
    return (
        <div>
            <div className="flex flex-wrap items-start gap-x-5 gap-y-2">
                <div>
                    <label className="text-[11px] font-medium text-gray-600 mb-1 block">Product</label>
                    <div className="flex gap-1.5">
                        {PRODUCT_TABS.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => onProduct(tab.id)}
                                className={`h-8 px-3 text-[11px] font-bold rounded-md transition-all ${product === tab.id ? 'bg-indigo-600 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="text-[11px] font-medium text-gray-600 mb-1 block">Applies to</label>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 min-h-8">
                        {SCOPE_OPTIONS[product].map((opt, i) => (
                            <label key={opt.label} className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                    type="radio"
                                    name={radioName}
                                    checked={scopeIdx === i}
                                    onChange={() => onScope(i)}
                                    className="w-3.5 h-3.5 text-indigo-600"
                                />
                                <span className="text-xs text-gray-700">{opt.label}</span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>
            <div className="mt-2">
                <button onClick={onToggleOldPlans} className="text-xs font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-0.5">
                    {showOldPlans ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
                    Also apply to old plans{selectedOldPlanIds.length > 0 ? ` (${selectedOldPlanIds.length} selected)` : ''}
                </button>
                {showOldPlans && (
                    <div className="mt-2 border border-gray-200 rounded-md p-2 max-h-40 overflow-y-auto space-y-1 bg-gray-50/50">
                        {loadingOldPlans ? (
                            <div className="flex justify-center py-2">
                                <CircularProgress size="sm" sx={{ '--CircularProgress-size': '16px' }} />
                            </div>
                        ) : oldPlanProducts.length === 0 ? (
                            <p className="text-xs text-gray-400 py-1">No products found</p>
                        ) : (
                            oldPlanProducts.map((prod) => (
                                <label key={prod.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded px-1 py-0.5">
                                    <input
                                        type="checkbox"
                                        checked={selectedOldPlanIds.includes(prod.id)}
                                        onChange={() => onToggleOldPlan(prod.id)}
                                        className="w-3.5 h-3.5 text-indigo-600"
                                    />
                                    <span className="text-xs text-gray-700">{prod.name}</span>
                                    <span className="text-[10px] font-mono text-gray-400">{prod.id}</span>
                                </label>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default function PromoCodesPage() {
    const [coupons, setCoupons] = React.useState<CouponSummary[]>([])
    /** '' = no choice yet (Create disabled); otherwise an existing coupon id. Codes are ALWAYS
     *  created under an explicitly chosen coupon — auto-minting one per code sprawled the
     *  catalog with single-use trash coupons ('+ Create new coupon' is the deliberate path). */
    const [selectedCouponId, setSelectedCouponId] = React.useState('')
    const [promoCodes, setPromoCodes] = React.useState<PromoCode[]>([])
    const [loadingCodes, setLoadingCodes] = React.useState(false)
    const [formCode, setFormCode] = React.useState('')
    const [formDiscount, setFormDiscount] = React.useState('20')
    // Default 'once' (first payment): a FOREVER discount minted by accident is a permanent revenue
    // leak, while under-discounting is a harmless re-create.
    const [formDuration, setFormDuration] = React.useState<'once' | 'repeating' | 'forever'>('once')
    const [formProduct, setFormProduct] = React.useState<PromoProduct>('extension')
    const [formScopeIdx, setFormScopeIdx] = React.useState(0)
    const [showOldPlans, setShowOldPlans] = React.useState(false)
    const [oldPlanProducts, setOldPlanProducts] = React.useState<StripeProductItem[]>([])
    const [loadingOldPlans, setLoadingOldPlans] = React.useState(false)
    const [oldPlanIds, setOldPlanIds] = React.useState<string[]>([])
    const [formBillingInterval, setFormBillingInterval] = React.useState<'both' | 'monthly' | 'annual'>('both')
    const [formMaxRedemptions, setFormMaxRedemptions] = React.useState('')
    const [formExpiresAt, setFormExpiresAt] = React.useState('')
    const [showNewCoupon, setShowNewCoupon] = React.useState(false)
    const [newCouponName, setNewCouponName] = React.useState('')
    const [couponType, setCouponType] = React.useState<'percent' | 'fixed'>('percent')
    const [couponDiscount, setCouponDiscount] = React.useState('20')
    const [couponDuration, setCouponDuration] = React.useState<'once' | 'repeating' | 'forever'>('once')
    const [couponDurationMonths, setCouponDurationMonths] = React.useState('3')
    const [couponMaxRedemptions, setCouponMaxRedemptions] = React.useState('')
    const [couponExpiresAt, setCouponExpiresAt] = React.useState('')
    const [couponNoCard, setCouponNoCard] = React.useState(false)
    const [couponTrialDays, setCouponTrialDays] = React.useState('60')
    const [creatingCoupon, setCreatingCoupon] = React.useState(false)
    const [formRequireCreditCard, setFormRequireCreditCard] = React.useState(true)
    const [formTrialDays, setFormTrialDays] = React.useState('60')
    const [creating, setCreating] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)
    const [success, setSuccess] = React.useState<string | null>(null)
    const [analyticsModal, setAnalyticsModal] = React.useState<PromoAnalytics | null>(null)
    const [loadingAnalytics, setLoadingAnalytics] = React.useState(false)
    const [copied, setCopied] = React.useState<string | null>(null)
    const [searchTable, setSearchTable] = React.useState('')
    const [statusFilter, setStatusFilter] = React.useState('all')

    const fetchCoupons = React.useCallback(async () => {
        try {
            const res = await axios.get('/gluon/billing/promo-coupons')
            setCoupons(res.data.data || [])
        } catch (err) {
            console.error('Failed to fetch coupons:', err)
        }
    }, [])
    const fetchPromoCodes = React.useCallback(async () => {
        setLoadingCodes(true)
        try {
            const res = await axios.get('/gluon/billing/promo-codes')
            setPromoCodes(res.data.data || [])
        } catch {
            // Keep the last successful list visible if refreshing fails.
        } finally {
            setLoadingCodes(false)
        }
    }, [])
    React.useEffect(() => {
        fetchCoupons()
        fetchPromoCodes()
    }, [fetchCoupons, fetchPromoCodes])

    const handleCreateCoupon = async () => {
        if (!newCouponName) return
        setCreatingCoupon(true)
        setError(null)
        try {
            const body: Record<string, unknown> = {
                name: newCouponName,
                duration: couponDuration,
                plans: formPlanTokens,
                requireCreditCard: !couponNoCard,
            }
            if (oldPlanIds.length > 0) body.extraProductIds = oldPlanIds
            if (!couponNoCard) {
                body.discountPercent = couponType === 'percent' ? parseInt(couponDiscount, 10) || 20 : undefined
                body.amountOff = couponType === 'fixed' ? Math.round(parseFloat(couponDiscount) * 100) : undefined
            } else {
                // No-card trial coupon: backend forces 100% off + once duration and tags trial_days.
                body.trialDays = parseInt(couponTrialDays, 10) || 60
            }
            if (couponDuration === 'repeating') body.durationInMonths = parseInt(couponDurationMonths, 10) || 3
            if (couponMaxRedemptions) body.maxRedemptions = parseInt(couponMaxRedemptions, 10)
            if (couponExpiresAt) body.expiresAt = couponExpiresAt
            const res = await axios.post('/gluon/billing/promo-coupons', body)
            setSelectedCouponId(res.data.data.id)
            setShowNewCoupon(false)
            setNewCouponName('')
            setCouponDiscount('20')
            setCouponMaxRedemptions('')
            setCouponExpiresAt('')
            setCouponNoCard(false)
            setCouponTrialDays('60')
            fetchCoupons()
            setSuccess(`Coupon "${res.data.data.name}" created!`)
        } catch (err: unknown) {
            const e = err as { response?: { data?: { message?: string } } }
            setError(e.response?.data?.message || 'Failed to create coupon')
        } finally {
            setCreatingCoupon(false)
        }
    }

    const handleCreatePromoCode = async () => {
        if (!formCode) return
        setCreating(true)
        setError(null)
        setSuccess(null)
        try {
            if (!selectedCouponId) return
            // Codes live under an explicitly chosen coupon — one offer, many codes, no sprawl.
            const body: Record<string, unknown> = {
                code: formCode.toUpperCase(),
                plans: formPlanTokens,
                billingInterval: formBillingInterval,
            }
            if (formMaxRedemptions) body.maxRedemptions = parseInt(formMaxRedemptions, 10)
            if (formExpiresAt) body.expiresAt = formExpiresAt
            await axios.post(`/gluon/billing/promo-coupons/${selectedCouponId}/codes`, body)
            setSuccess(`Promo code ${formCode.toUpperCase()} created! Subscribe link: ${getSubscribeBase()}/subscribe?promo=${formCode.toUpperCase()}`)
            setFormCode('')
            setFormMaxRedemptions('')
            setFormExpiresAt('')
            fetchPromoCodes()
            fetchCoupons()
        } catch (err: unknown) {
            const e = err as { response?: { data?: { message?: string } } }
            setError(e.response?.data?.message || 'Failed to create promo code')
        } finally {
            setCreating(false)
        }
    }

    const handleDeactivate = async (id: string) => {
        try {
            await axios.post(`/gluon/billing/promo-codes/${id}/deactivate`)
            fetchPromoCodes()
        } catch {
            setError('Failed to deactivate')
        }
    }
    const handleViewAnalytics = async (id: string) => {
        setLoadingAnalytics(true)
        setAnalyticsModal(null)
        try {
            const res = await axios.get(`/gluon/billing/promo-codes/${id}/analytics`)
            setAnalyticsModal(res.data.data)
        } catch {
            setError('Failed to load analytics')
        } finally {
            setLoadingAnalytics(false)
        }
    }
    const copyToClipboard = (text: string, id: string) => {
        navigator.clipboard.writeText(text)
        setCopied(id)
        setTimeout(() => setCopied(null), 2000)
    }
    const formPlanTokens = SCOPE_OPTIONS[formProduct][formScopeIdx]?.tokens ?? SCOPE_OPTIONS[formProduct][0].tokens
    const handleProductChange = (p: PromoProduct) => {
        setFormProduct(p)
        setFormScopeIdx(0) // reset applies-to to the product's first option
    }
    const toggleOldPlan = (id: string) => setOldPlanIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
    const handleToggleOldPlans = () => {
        const next = !showOldPlans
        setShowOldPlans(next)
        if (next && oldPlanProducts.length === 0 && !loadingOldPlans) {
            setLoadingOldPlans(true)
            billingApi
                .getProducts()
                .then((items) => setOldPlanProducts(items))
                .catch(() => {})
                .finally(() => setLoadingOldPlans(false))
        }
    }
    /** Best-effort: point product/applies-to at the selected coupon's scope (legacy collapsed 'TMS' means both TMS tiers). */
    const syncScopeFromPlans = (plans: string[]) => {
        const normalized = plans.flatMap((t) => (t === 'TMS' ? ['TMS_BASIC', 'TMS_PRO'] : [t]))
        if (normalized.length === 0) return
        for (const tab of PRODUCT_TABS) {
            const idx = SCOPE_OPTIONS[tab.id].findIndex((o) => o.tokens.length === normalized.length && o.tokens.every((t) => normalized.includes(t)))
            if (idx >= 0) {
                setFormProduct(tab.id)
                setFormScopeIdx(idx)
                return
            }
        }
    }

    const filteredCodes = React.useMemo(
        () =>
            promoCodes.filter((c) => {
                if (
                    searchTable &&
                    !c.code.toLowerCase().includes(searchTable.toLowerCase()) &&
                    !c.influencerName.toLowerCase().includes(searchTable.toLowerCase())
                )
                    return false
                if (statusFilter === 'active' && !c.active) return false
                if (statusFilter === 'inactive' && c.active) return false
                return true
            }),
        [promoCodes, searchTable, statusFilter]
    )

    const hasTableFilters = searchTable || statusFilter !== 'all'

    return (
        <div className="p-6 w-full">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-5">
                <span>Home</span>
                <span>/</span>
                <span className="text-gray-600 font-medium">Promo Codes</span>
            </div>

            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease }}
                className="flex items-center gap-3 mb-6"
            >
                <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
                    <LocalOffer sx={{ fontSize: 18 }} className="text-orange-600" />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-gray-900">Promo Codes</h1>
                    <p className="text-sm text-gray-500">Create and manage Stripe promotion codes</p>
                </div>
            </motion.div>

            {/* Create Form + Live Preview */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1, ease }}>
                <div className="grid grid-cols-3 gap-4 mb-6">
                    {/* Form (2/3) */}
                    <div className="col-span-2 bg-white rounded-xl shadow-sm ring-1 ring-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-5 py-3.5">
                            <h3 className="text-white font-semibold text-sm">Create Promo Code</h3>
                            <p className="text-indigo-200 text-[11px] mt-0.5">
                                Pick the coupon (the offer) this code sells under — or create a new coupon first
                            </p>
                        </div>
                        <div className="p-4">
                            {/* Row 1: Coupon + Promo Code */}
                            <div className="grid grid-cols-4 gap-3 mb-3">
                                <div>
                                    <label className="text-[11px] font-medium text-gray-600 mb-1 block">Coupon *</label>
                                    {!showNewCoupon ? (
                                        <select
                                            value={selectedCouponId}
                                            onChange={(e) => {
                                                if (e.target.value === '__new__') {
                                                    setShowNewCoupon(true)
                                                    setSelectedCouponId('')
                                                } else {
                                                    setSelectedCouponId(e.target.value)
                                                    if (e.target.value) {
                                                        const sel = coupons.find((c) => c.id === e.target.value)
                                                        if (sel) {
                                                            setFormDiscount(String(sel.discountPercent))
                                                            setFormDuration(sel.duration as 'once' | 'repeating' | 'forever')
                                                            syncScopeFromPlans(sel.plans || [])
                                                            setFormRequireCreditCard(!sel.trialDays)
                                                            if (sel.trialDays) setFormTrialDays(String(sel.trialDays))
                                                        }
                                                    }
                                                }
                                            }}
                                            className="w-full h-8 text-sm px-2 border border-gray-200 rounded-md bg-white"
                                        >
                                            <option value="" disabled>
                                                — select —
                                            </option>

                                            {coupons.map((c) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name} — {c.codeCount} codes
                                                </option>
                                            ))}
                                            <option value="__new__">+ Create new coupon</option>
                                        </select>
                                    ) : null}
                                </div>
                                <div>
                                    <label className="text-[11px] font-medium text-gray-600 mb-1 block">Promo Code</label>
                                    <input
                                        placeholder="e.g. JACK30"
                                        value={formCode}
                                        onChange={(e) => setFormCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
                                        className="w-full h-8 text-sm font-mono px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                    />
                                </div>
                            </div>
                            {/* Row 2: Product + Applies to + old plans */}
                            <div className="pt-3 border-t border-gray-100 mb-3">
                                <ScopeSelector
                                    radioName="formScope"
                                    product={formProduct}
                                    scopeIdx={formScopeIdx}
                                    onProduct={handleProductChange}
                                    onScope={setFormScopeIdx}
                                    showOldPlans={showOldPlans}
                                    onToggleOldPlans={handleToggleOldPlans}
                                    oldPlanProducts={oldPlanProducts}
                                    loadingOldPlans={loadingOldPlans}
                                    selectedOldPlanIds={oldPlanIds}
                                    onToggleOldPlan={toggleOldPlan}
                                />
                            </div>
                            {/* Row 3: Max Redemptions + Expires + Billing + Create */}
                            <div className="flex items-end gap-3 pt-3 border-t border-gray-100">
                                <div className="flex-1">
                                    <label className="text-[11px] font-medium text-gray-600 mb-1 block">Max Redemptions</label>
                                    <input
                                        type="number"
                                        placeholder="Unlimited"
                                        value={formMaxRedemptions}
                                        onChange={(e) => setFormMaxRedemptions(e.target.value)}
                                        className="w-full h-8 text-sm px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="text-[11px] font-medium text-gray-600 mb-1 block">Expires At</label>
                                    <input
                                        type="date"
                                        value={formExpiresAt}
                                        onChange={(e) => setFormExpiresAt(e.target.value)}
                                        className="w-full h-8 text-sm px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] font-medium text-gray-600 mb-1 block">Billing</label>
                                    <select
                                        value={formBillingInterval}
                                        onChange={(e) => setFormBillingInterval(e.target.value as 'both' | 'monthly' | 'annual')}
                                        className="h-8 text-[11px] px-2 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                    >
                                        <option value="both">Monthly &amp; Annual</option>
                                        <option value="monthly">Monthly only</option>
                                        <option value="annual">Annual only</option>
                                    </select>
                                </div>
                                <div className="ml-auto">
                                    <label className="text-[11px] font-medium text-gray-600 mb-1 block">&nbsp;</label>
                                    <button
                                        onClick={handleCreatePromoCode}
                                        disabled={creating || !formCode || !selectedCouponId}
                                        className="h-8 px-5 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                                    >
                                        {creating ? (
                                            <CircularProgress size="sm" sx={{ '--CircularProgress-size': '14px' }} />
                                        ) : (
                                            <LocalOffer sx={{ fontSize: 14 }} />
                                        )}
                                        Create Code
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Live Preview (1/3) */}
                    <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-200">
                        <div className="px-4 py-3 border-b border-gray-100">
                            <p className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                                <Visibility sx={{ fontSize: 12 }} className="text-gray-400" /> Live Preview
                            </p>
                            <p className="text-[11px] text-gray-400">How this code will appear</p>
                        </div>
                        <div className="p-4">
                            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-dashed border-gray-200">
                                <div className="bg-white rounded-lg border shadow-sm p-3 space-y-2.5">
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded bg-indigo-100 flex items-center justify-center">
                                            <LocalOffer sx={{ fontSize: 12 }} className="text-indigo-600" />
                                        </div>
                                        <span className="font-mono text-sm font-bold text-gray-800">{formCode || 'CODE'}</span>
                                    </div>
                                    <div className="space-y-1.5">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] text-gray-400 uppercase tracking-wider">Discount</span>
                                            <span className="text-sm font-bold text-emerald-600">{parseInt(formDiscount) || 0}% off</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] text-gray-400 uppercase tracking-wider">Duration</span>
                                            <span className="text-xs text-gray-700 capitalize">{formDuration}</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] text-gray-400 uppercase tracking-wider">Plans</span>
                                            <ScopeBadges plans={formPlanTokens} />
                                        </div>
                                        {oldPlanIds.length > 0 && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-[10px] text-gray-400 uppercase tracking-wider">Old plans</span>
                                                <span className="text-[9px] font-bold px-1.5 py-0 rounded bg-gray-100 text-gray-600">
                                                    +{oldPlanIds.length} old plan{oldPlanIds.length > 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] text-gray-400 uppercase tracking-wider">Limit</span>
                                            <span className="text-xs text-gray-700">{formMaxRedemptions || 'Unlimited'}</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] text-gray-400 uppercase tracking-wider">Credit card</span>
                                            <span className={`text-xs font-medium ${formRequireCreditCard ? 'text-gray-700' : 'text-amber-600'}`}>
                                                {formRequireCreditCard ? 'Required' : `No — ${formTrialDays}-day trial`}
                                            </span>
                                        </div>
                                        {formExpiresAt && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-[10px] text-gray-400 uppercase tracking-wider">Expires</span>
                                                <span className="text-xs text-gray-700">{formExpiresAt}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Alerts */}
            {error && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 flex items-center justify-between">
                    {error}
                    <button onClick={() => setError(null)}>
                        <Close sx={{ fontSize: 14 }} />
                    </button>
                </div>
            )}
            {success && (
                <div className="bg-emerald-50 text-emerald-700 px-4 py-2 rounded-lg text-sm mb-4 flex items-center gap-2">
                    <CheckCircle sx={{ fontSize: 16 }} />
                    {success}
                </div>
            )}

            {/* Promo Codes Table */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2, ease }}>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                    <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                        <LocalOffer sx={{ fontSize: 16 }} className="text-gray-500" />
                        <p className="text-sm font-semibold text-gray-900">Promo Codes</p>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{filteredCodes.length}</span>
                    </div>

                    {/* Filters */}
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex gap-3 items-end">
                        <div className="flex-1 max-w-sm relative">
                            <input
                                placeholder="Search by code or influencer..."
                                value={searchTable}
                                onChange={(e) => setSearchTable(e.target.value)}
                                className="w-full h-9 text-sm pl-3 pr-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            />
                        </div>
                        <div className="w-32">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full h-9 text-sm px-2 border border-gray-200 rounded-lg bg-white"
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                        {hasTableFilters && (
                            <button
                                onClick={() => {
                                    setSearchTable('')
                                    setStatusFilter('all')
                                }}
                                className="h-9 px-3 text-xs text-gray-500 hover:text-red-600 flex items-center gap-1"
                            >
                                <Close sx={{ fontSize: 13 }} /> Clear
                            </button>
                        )}
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50/80 text-left">
                                    <th className="px-5 py-3 text-xs font-semibold text-gray-600">Code</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Influencer</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Discount</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Duration</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Plans</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Used</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Status</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Expires</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-right pr-5">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingCodes ? (
                                    <tr>
                                        <td colSpan={9} className="text-center py-12">
                                            <CircularProgress size="sm" />
                                        </td>
                                    </tr>
                                ) : filteredCodes.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="text-center py-12 text-gray-400 text-sm">
                                            {hasTableFilters ? 'No codes match your filters' : 'No promo codes created yet'}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredCodes.map((pc, idx) => (
                                        <motion.tr
                                            key={pc.id}
                                            initial={{ opacity: 0, x: -8 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.2, delay: idx * 0.03, ease }}
                                            className="hover:bg-gray-50/50 transition-colors border-b border-gray-100 last:border-b-0 group"
                                        >
                                            <td className="px-5 py-2.5 font-mono font-medium text-gray-800">{pc.code}</td>
                                            <td className="px-2 py-2.5">
                                                <div>
                                                    <p className="text-gray-700">{pc.influencerName}</p>
                                                    <p className="text-[10px] text-gray-400">
                                                        {pc.influencerTier} ({pc.commissionPercent}%)
                                                    </p>
                                                </div>
                                            </td>
                                            <td className="px-2 py-2.5">
                                                {pc.trialDays ? (
                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-50 text-amber-700">
                                                        {pc.trialDays}-day trial
                                                    </span>
                                                ) : (
                                                    <span className="text-sm font-semibold text-emerald-600">{pc.discountPercent}% off</span>
                                                )}
                                            </td>
                                            <td className="px-2 py-2.5 text-gray-600">{formatDuration(pc.duration, pc.durationInMonths)}</td>
                                            <td className="px-2 py-2.5">
                                                <ScopeBadges plans={pc.plans} />
                                            </td>
                                            <td className="px-2 py-2.5">
                                                <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                                                    {pc.timesRedeemed}
                                                    {pc.maxRedemptions ? `/${pc.maxRedemptions}` : ''}
                                                </span>
                                            </td>
                                            <td className="px-2 py-2.5">
                                                <span
                                                    className={`text-[10px] px-2 py-0.5 rounded inline-flex items-center gap-1 ${pc.active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}
                                                >
                                                    <span className={`w-1.5 h-1.5 rounded-full ${pc.active ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                                                    {pc.active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-2 py-2.5 text-gray-500 text-xs">{pc.expiresAt ? formatDate(pc.expiresAt) : 'Never'}</td>
                                            <td className="px-2 py-2.5 text-right pr-5">
                                                <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={() => copyToClipboard(`${getSubscribeBase()}/subscribe?promo=${pc.code}`, pc.id)}
                                                        className="w-7 h-7 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 inline-flex items-center justify-center"
                                                        title={copied === pc.id ? 'Copied!' : 'Copy link'}
                                                    >
                                                        <ContentCopy sx={{ fontSize: 13 }} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleViewAnalytics(pc.id)}
                                                        className="w-7 h-7 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 inline-flex items-center justify-center"
                                                        title="Analytics"
                                                    >
                                                        <BarChart sx={{ fontSize: 13 }} />
                                                    </button>
                                                    {pc.active && (
                                                        <button
                                                            onClick={() => handleDeactivate(pc.id)}
                                                            className="w-7 h-7 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 inline-flex items-center justify-center"
                                                            title="Deactivate"
                                                        >
                                                            <Block sx={{ fontSize: 13 }} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </motion.div>

            {/* Create Coupon Modal */}
            <Modal open={showNewCoupon} onClose={() => setShowNewCoupon(false)}>
                <ModalDialog sx={{ maxWidth: 520, borderRadius: 'lg' }}>
                    <ModalClose />
                    <h3 className="text-lg font-bold text-gray-900">Create coupon</h3>
                    <p className="text-sm text-gray-500 mb-5">Coupons can be used to discount invoices, subscriptions, or entire customer accounts.</p>
                    <div className="space-y-5">
                        {/* Name */}
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Name</label>
                            <input
                                placeholder="First purchase discount"
                                value={newCouponName}
                                onChange={(e) => setNewCouponName(e.target.value)}
                                className="w-full h-10 text-sm px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                            />
                            <p className="text-[11px] text-gray-400 mt-1">This will appear on customers' receipts and invoices.</p>
                        </div>

                        {/* No card (free trial) */}
                        <div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={couponNoCard}
                                    onChange={(e) => setCouponNoCard(e.target.checked)}
                                    className="w-4 h-4 text-indigo-600"
                                />
                                <span className="text-sm font-medium text-gray-700">No card — free trial coupon</span>
                            </label>
                            <p className="text-[11px] text-gray-400 mt-1 ml-6">
                                {couponNoCard
                                    ? 'No card needed — redeeming the code grants a new trial (free users) or extends an existing one.'
                                    : 'Standard discount coupon — applied at checkout.'}
                            </p>
                        </div>

                        {!couponNoCard ? (
                            <>
                                {/* Type */}
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-2 block">Type</label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="couponType"
                                                checked={couponType === 'percent'}
                                                onChange={() => setCouponType('percent')}
                                                className="w-4 h-4 text-indigo-600"
                                            />
                                            <span className="text-sm text-gray-700">Percentage off</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="couponType"
                                                checked={couponType === 'fixed'}
                                                onChange={() => setCouponType('fixed')}
                                                className="w-4 h-4 text-indigo-600"
                                            />
                                            <span className="text-sm text-gray-700">Fixed amount off</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Discount amount */}
                                <div>
                                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                                        {couponType === 'percent' ? 'Percentage discount' : 'Discount amount'}
                                    </label>
                                    <div className="flex items-center gap-2">
                                        {couponType === 'fixed' && <span className="text-sm text-gray-500">$</span>}
                                        <input
                                            type="number"
                                            value={couponDiscount}
                                            onChange={(e) => setCouponDiscount(e.target.value)}
                                            min={couponType === 'percent' ? '1' : '0.01'}
                                            max={couponType === 'percent' ? '100' : '99999'}
                                            step={couponType === 'fixed' ? '0.01' : '1'}
                                            className="w-32 h-10 text-sm px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                                        />
                                        {couponType === 'percent' && <span className="text-sm text-gray-500">%</span>}
                                        {couponType === 'fixed' && <span className="text-sm text-gray-500">USD</span>}
                                    </div>
                                </div>
                            </>
                        ) : (
                            /* Trial period duration (no-card) */
                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Trial period duration</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        value={couponTrialDays}
                                        onChange={(e) => setCouponTrialDays(e.target.value)}
                                        min="1"
                                        max="365"
                                        autoFocus
                                        className="w-24 h-10 text-sm px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                                    />
                                    <span className="text-sm text-gray-500">days</span>
                                </div>
                                <p className="text-[11px] text-gray-400 mt-1">
                                    Enter how long the free trial lasts. Free users get a new Pro trial; users already on trial get it extended.
                                </p>
                            </div>
                        )}

                        {/* Duration (discount coupons only — no-card trials are always "once") */}
                        {!couponNoCard && (
                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Duration</label>
                                <select
                                    value={couponDuration}
                                    onChange={(e) => setCouponDuration(e.target.value as 'once' | 'repeating' | 'forever')}
                                    className="w-48 h-10 text-sm px-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                                >
                                    <option value="once">Once</option>
                                    <option value="repeating">Multiple months</option>
                                    <option value="forever">Forever</option>
                                </select>
                                {couponDuration === 'repeating' && (
                                    <div className="flex items-center gap-2 mt-2">
                                        <input
                                            type="number"
                                            value={couponDurationMonths}
                                            onChange={(e) => setCouponDurationMonths(e.target.value)}
                                            min="1"
                                            max="36"
                                            className="w-20 h-9 text-sm px-3 border border-gray-300 rounded-lg"
                                        />
                                        <span className="text-sm text-gray-500">months</span>
                                    </div>
                                )}
                                <p className="text-[11px] text-gray-400 mt-1">For subscriptions, this determines how long the coupon applies once redeemed.</p>
                            </div>
                        )}

                        {/* Product scope */}
                        <div>
                            <ScopeSelector
                                radioName="couponScope"
                                product={formProduct}
                                scopeIdx={formScopeIdx}
                                onProduct={handleProductChange}
                                onScope={setFormScopeIdx}
                                showOldPlans={showOldPlans}
                                onToggleOldPlans={handleToggleOldPlans}
                                oldPlanProducts={oldPlanProducts}
                                loadingOldPlans={loadingOldPlans}
                                selectedOldPlanIds={oldPlanIds}
                                onToggleOldPlan={toggleOldPlan}
                            />
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                            <button
                                onClick={() => setShowNewCoupon(false)}
                                className="h-9 px-4 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateCoupon}
                                disabled={creatingCoupon || !newCouponName || (couponNoCard ? !couponTrialDays : !couponDiscount)}
                                className="h-9 px-5 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                            >
                                {creatingCoupon ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '14px' }} /> : null}
                                Create coupon
                            </button>
                        </div>
                    </div>
                </ModalDialog>
            </Modal>

            {/* Analytics Modal (MUI Joy) */}
            <Modal open={analyticsModal !== null || loadingAnalytics} onClose={() => setAnalyticsModal(null)}>
                <ModalDialog sx={{ maxWidth: 700, maxHeight: '80vh', overflow: 'auto', borderRadius: 'lg' }}>
                    <ModalClose />
                    {loadingAnalytics ? (
                        <div className="flex justify-center py-12">
                            <CircularProgress />
                        </div>
                    ) : analyticsModal ? (
                        <>
                            <h3 className="text-lg font-bold text-gray-900 mb-0.5">Analytics: {analyticsModal.code}</h3>
                            <p className="text-sm text-gray-500 mb-4">
                                Influencer: {analyticsModal.influencerName} ({analyticsModal.commissionPercent}% commission)
                            </p>
                            <div className="grid grid-cols-4 gap-3 mb-4">
                                <div className="bg-gray-50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-gray-900">{analyticsModal.totalSignups}</p>
                                    <p className="text-[10px] text-gray-500">Total Signups</p>
                                </div>
                                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-emerald-700">{formatCurrency(analyticsModal.totalRevenue)}</p>
                                    <p className="text-[10px] text-gray-500">Revenue</p>
                                </div>
                                <div className="bg-blue-50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(analyticsModal.totalCommission)}</p>
                                    <p className="text-[10px] text-gray-500">Commission</p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3 text-center">
                                    <p className="text-lg font-bold text-gray-700">
                                        {Object.entries(analyticsModal.byPlan)
                                            .map(([p, c]) => `${p}: ${c}`)
                                            .join(', ') || '—'}
                                    </p>
                                    <p className="text-[10px] text-gray-500">By Plan</p>
                                </div>
                            </div>
                            {analyticsModal.users.length > 0 && (
                                <>
                                    <p className="text-sm font-semibold text-gray-900 mb-2">Users ({analyticsModal.users.length})</p>
                                    <div className="overflow-x-auto border rounded-lg">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="bg-gray-50/80 text-left">
                                                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Email</th>
                                                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Company</th>
                                                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Plan</th>
                                                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Revenue</th>
                                                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Commission</th>
                                                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Joined</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {analyticsModal.users.map((u, i) => (
                                                    <tr key={i} className="border-b border-gray-100 last:border-b-0">
                                                        <td className="px-3 py-2 text-xs">{u.email}</td>
                                                        <td className="px-3 py-2 text-xs text-gray-500">{u.projectName}</td>
                                                        <td className="px-3 py-2">
                                                            <span
                                                                className={`text-[9px] font-bold px-1.5 py-0 rounded ${u.plan === 'ULTRA' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}
                                                            >
                                                                {u.plan}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 text-xs font-semibold">{formatCurrency(u.price)}</td>
                                                        <td className="px-3 py-2 text-xs font-semibold text-blue-600">{formatCurrency(u.commission)}</td>
                                                        <td className="px-3 py-2 text-xs text-gray-500">
                                                            {u.joinedAt
                                                                ? new Date(u.joinedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                                                : '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                            {analyticsModal.users.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No signups yet</p>}
                        </>
                    ) : null}
                </ModalDialog>
            </Modal>
        </div>
    )
}
